"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CheckCircle2, UploadCloud } from "lucide-react";
export default function SubmitANewTip() {
  return <section id="submit-a-new-tip" className="bg-[#E6D5C3] py-[160px] relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-[600px] mx-auto">
          <h2 className="text-[48px] font-default font-bold leading-tight mb-10 text-left text-zinc-900 tracking-tight">
            Share your Zen
          </h2>
          
          <FormProvider formId="tip_submission_smarthometipsandtricks" sectionName="Submit a New Tip" defaultValues={{
          title: "",
          description: "",
          category: "",
          author_name: "",
          author_email: "",
          image: ""
        }} validationRules={{
          title: {
            required: true
          },
          description: {
            required: true
          },
          category: {
            required: true
          },
          author_name: {
            required: true
          },
          author_email: {
            required: true,
            pattern: "email"
          },
          image: {
            required: false
          }
        }} submitText="Submit Tip" successMessage="Your tip has been successfully submitted and is pending review.">
            <TipSubmissionForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function TipSubmissionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="backdrop-blur-lg bg-white/40 border border-white/50 rounded-[32px] p-12 text-center shadow-xl animate-in fade-in zoom-in duration-500">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/60 mb-6 shadow-sm">
          <CheckCircle2 className="w-10 h-10 text-zinc-900" />
        </div>
        <h3 className="text-3xl font-default font-semibold text-zinc-900 mb-3">Thank You!</h3>
        <p className="text-zinc-800 text-[17px] mb-8 max-w-md mx-auto leading-relaxed">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-full h-12 px-8 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors text-[17px]">
          Submit Another Tip
        </Button>
      </div>;
  }
  const inputClasses = "bg-black/5 border-transparent shadow-none text-[17px] focus-visible:bg-white/50 focus-visible:border-zinc-400 focus-visible:ring-zinc-400/20 transition-all duration-200";
  const labelClasses = "text-zinc-800 font-medium mb-1";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="title" rules={{
      required: "Tip Title is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className={labelClasses}>Tip Title</FormLabel>
            <FormControl>
              <Input placeholder="e.g., How to set up HomeKit lighting scenes" className={`h-12 ${inputClasses}`} {...field} />
            </FormControl>
            <FormDescription className="text-zinc-600 text-sm">
              Give a short, descriptive title for your tip.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="description" rules={{
      required: "Tip Description is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className={labelClasses}>Tip Description</FormLabel>
            <FormControl>
              <Textarea placeholder="Explain the steps, settings, or ideas in detail." className={`min-h-[140px] resize-y py-3 ${inputClasses}`} {...field} />
            </FormControl>
            <FormDescription className="text-zinc-600 text-sm">
              Provide a clear, step-by-step guide or explanation.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="category" rules={{
      required: "Please select a category"
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className={labelClasses}>Category</FormLabel>
            <FormControl>
              <div className="relative">
                <select className={`flex h-12 w-full appearance-none rounded-md px-3 py-2 outline-none disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses}`} {...field}>
                  <option value="" disabled>Select a category</option>
                  <option value="Lighting">Lighting</option>
                  <option value="Security">Security</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Climate">Climate</option>
                  <option value="Other">Other</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-zinc-500">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormDescription className="text-zinc-600 text-sm">
              Choose the area of home automation this tip applies to.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="author_name" rules={{
        required: "Your Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className={labelClasses}>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className={`h-12 ${inputClasses}`} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="author_email" rules={{
        required: "Your Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1.5">
              <FormLabel className={labelClasses}>Your Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className={`h-12 ${inputClasses}`} {...field} />
              </FormControl>
              <FormDescription className="text-zinc-600 text-sm">
                We'll use this to contact you if we need clarification.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="image" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="space-y-1.5">
            <FormLabel className={labelClasses}>Optional Image</FormLabel>
            <FormControl>
              <div className={`relative flex items-center justify-center w-full h-32 border-2 border-dashed border-zinc-300 rounded-xl bg-black/5 hover:bg-black/10 transition-colors cursor-pointer overflow-hidden group`}>
                <Input type="file" accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => {
            onChange(e.target.files);
          }} {...field} />
                <div className="flex flex-col items-center justify-center text-zinc-500 group-hover:text-zinc-700 transition-colors">
                  <UploadCloud className="w-8 h-8 mb-2" />
                  <span className="text-sm font-medium">Click to upload or drag and drop</span>
                </div>
              </div>
            </FormControl>
            <FormDescription className="text-zinc-600 text-sm">
              Upload a screenshot or photo that helps illustrate your tip.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-xl text-red-800 text-sm animate-in fade-in">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full rounded-full bg-zinc-900 text-white hover:bg-zinc-800 h-14 text-[17px] font-medium shadow-lg shadow-zinc-900/20 transition-all hover:shadow-zinc-900/30 hover:-translate-y-0.5">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting Tip...
            </> : 'Submit Tip'}
        </Button>
      </div>
    </Form>;
}